import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Archive, ExternalLink, Eye, Link2Off, MapPin, MoreHorizontal, Pencil, RotateCcw, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import Pagination from '@/client/components/common/Pagination';
import { useListFilters } from '@/client/hooks/useListFilters';
import { useGate } from '@/client/hooks/useGate';
import { PERMISSIONS } from '@/client/config/permissions';
import { useVirtualToursQuery, VirtualTour } from '@/client/api/listing-management/virtual-tours.query';
import { getApiErrorMessage } from '@/client/utils/errors';
import { EligibilityBadges, fmtDate, matterportShowUrl, StatusBadge, useListingCover } from './shared';
import { MatterportThumb, ModelPreviewDialog } from './ModelPreview';
import { TourDetailDrawer } from './TourDetailDrawer';
import { AddressVisibilityDialog, ArchiveDialog, ReactivateDialog, RenameDialog, UnlinkDialog, VisibilityDialog } from './TourActionDialogs';

const STATUS_OPTIONS = ['', 'processing', 'active', 'inactive', 'archived', 'failed', 'quota_blocked', 'unlinked'];
const SOURCE_OPTIONS = ['', 'webhook', 'reconcile', 'dispatch', 'admin', 'admin_bypass', 'seller', 'backfill'];
const selectClass = 'h-9 rounded-md border border-input bg-background px-2 text-sm text-gray-900';

const FilterSelect: React.FC<{ label: string; value: string; onChange: (value: string) => void; children: React.ReactNode }> = ({
  label,
  value,
  onChange,
  children,
}) => (
  <label className="flex items-center gap-1.5 text-xs text-gray-500">
    <span className="shrink-0">{label}</span>
    <select className={selectClass} aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}>
      {children}
    </select>
  </label>
);

const TourListing: React.FC<{ tour: VirtualTour; onOpen: () => void; onPreview: () => void }> = ({ tour, onOpen, onPreview }) => {
  const cover = useListingCover(tour.eid);
  const label = tour.listing_name || (tour.eid != null ? `#${tour.eid}` : 'Not linked');
  return (
    <div className="flex min-w-0 items-center gap-3">
      <MatterportThumb id={tour.external_id} alt={label} fallbackSrc={cover} onClick={tour.external_id ? onPreview : undefined} />
      <div className="min-w-0">
        {tour.eid != null ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            className="block max-w-md truncate text-left text-sm text-blue-600 hover:underline"
          >
            {label}
          </button>
        ) : (
          <span className="text-sm text-gray-400">Not linked</span>
        )}
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {tour.eid != null && (
            <span
              className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                tour.listing_live ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
              }`}
            >
              {tour.listing_live ? 'Live' : 'Not live'}
            </span>
          )}
          <EligibilityBadges planEligible={tour.plan_eligible} source={tour.source} />
          {tour.visibility && <StatusBadge status={tour.visibility} />}
        </div>
      </div>
    </div>
  );
};

const RowMenu: React.FC<{ tour: VirtualTour; onAct: (kind: 'visibility' | 'details' | 'address' | 'archive' | 'reactivate' | 'unlink', tour: VirtualTour) => void }> = ({ tour, onAct }) => {
  const publish = useGate(PERMISSIONS.virtualTour.publish).allowed;
  const edit = useGate(PERMISSIONS.virtualTour.edit).allowed;
  const link = useGate(PERMISSIONS.virtualTour.link).allowed;
  const archive = useGate(PERMISSIONS.virtualTour.archive).allowed;
  const reactivate = useGate(PERMISSIONS.virtualTour.reactivate).allowed;
  const matterport = tour.provider === 'matterport' && !!tour.external_id;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Actions">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {matterport && (
          <DropdownMenuItem asChild>
            <a href={matterportShowUrl(tour.external_id as string)} target="_blank" rel="noopener noreferrer">
              <ExternalLink /> Open in Matterport
            </a>
          </DropdownMenuItem>
        )}
        {matterport && (
          <DropdownMenuItem disabled={!publish} onClick={() => onAct('visibility', tour)}>
            <Eye /> Visibility
          </DropdownMenuItem>
        )}
        {matterport && (
          <DropdownMenuItem disabled={!edit} onClick={() => onAct('details', tour)}>
            <Pencil /> Name and description
          </DropdownMenuItem>
        )}
        {matterport && (
          <DropdownMenuItem disabled={!publish} onClick={() => onAct('address', tour)}>
            <MapPin /> Address visibility
          </DropdownMenuItem>
        )}
        {tour.eid != null && (
          <DropdownMenuItem disabled={!link} onClick={() => onAct('unlink', tour)}>
            <Link2Off /> Unlink
          </DropdownMenuItem>
        )}
        {tour.status === 'active' && (
          <DropdownMenuItem disabled={!archive} onClick={() => onAct('archive', tour)}>
            <Archive /> Archive
          </DropdownMenuItem>
        )}
        {tour.status === 'archived' && (
          <DropdownMenuItem disabled={!reactivate} onClick={() => onAct('reactivate', tour)}>
            <RotateCcw /> Reactivate
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ToursTab: React.FC<{ scope: string }> = ({ scope }) => {
  const navigate = useNavigate();
  const { searchTerm, debouncedSearchTerm, filters, page, limit, handleSearch, handleFilterChange, handlePageChange, handleLimitChange } =
    useListFilters({
      initialFilters: { status: '', provider: '', source: '', eligibility: '' },
      namespace: 'tours',
    });

  // The open tour lives in `?tour=<vtid>` so other tabs can deep-link to a drawer.
  const [searchParams, setSearchParams] = useSearchParams();
  const [act, setAct] = useState<{ kind: 'visibility' | 'details' | 'address' | 'archive' | 'reactivate' | 'unlink'; tour: VirtualTour } | null>(null);
  const [preview, setPreview] = useState<VirtualTour | null>(null);
  const openVtid = Number(searchParams.get('tour')) || null;
  const setOpenVtid = (vtid: number | null) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (vtid == null) next.delete('tour');
        else next.set('tour', String(vtid));
        return next;
      },
      { replace: true }
    );

  const { data, isLoading, isFetching, isError, error } = useVirtualToursQuery({
    status: filters.status || undefined,
    provider: filters.provider || undefined,
    source: filters.source || undefined,
    eligibility: filters.eligibility || undefined,
    q: debouncedSearchTerm || undefined,
    scope,
    page,
    page_size: limit,
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            className="pl-9"
            placeholder="Search by listing name or external id…"
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        <FilterSelect label="Status" value={filters.status} onChange={(v) => handleFilterChange('status', v)}>
          {STATUS_OPTIONS.map((s) => (
            <option key={s || 'any'} value={s}>
              {s ? s.replace(/_/g, ' ') : 'Any'}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect label="Provider" value={filters.provider} onChange={(v) => handleFilterChange('provider', v)}>
          <option value="">Any</option>
          <option value="matterport">Matterport</option>
          <option value="url">URL</option>
        </FilterSelect>
        <FilterSelect label="Source" value={filters.source} onChange={(v) => handleFilterChange('source', v)}>
          {SOURCE_OPTIONS.map((s) => (
            <option key={s || 'any'} value={s}>
              {s ? s.replace(/_/g, ' ') : 'Any'}
            </option>
          ))}
        </FilterSelect>
        <FilterSelect label="Plan" value={filters.eligibility} onChange={(v) => handleFilterChange('eligibility', v)}>
          <option value="">Any</option>
          <option value="ineligible">No eligible plan</option>
        </FilterSelect>
        {isFetching && <span className="text-xs text-gray-400">Loading…</span>}
      </div>

      {isError ? (
        <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-6 text-sm text-red-600">
          {getApiErrorMessage(error, 'Could not load tours.')}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Listing</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Model</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading && !data
                ? Array.from({ length: limit }).map((_, i) => (
                    <tr key={i}>
                      <td className="px-4 py-3" colSpan={4}>
                        <Skeleton className="h-5 w-full" />
                      </td>
                    </tr>
                  ))
                : (data?.tours ?? []).map((t) => (
                    <tr
                      key={t.vtid}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={(e) => {
                        if ((e.target as HTMLElement).closest('button, a')) return;
                        setOpenVtid(t.vtid);
                      }}
                    >
                      <td className="px-4 py-2">
                        <TourListing
                          tour={t}
                          onOpen={() => navigate(`/admin/listings/${t.eid}`)}
                          onPreview={() => setPreview(t)}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-mono text-xs text-gray-500">{t.external_id || '—'}</div>
                        {(t.provider !== 'matterport' || (t.source && t.source !== 'backfill') || t.last_synced_at) && (
                          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-gray-400">
                            {t.provider !== 'matterport' && <span>{t.provider}</span>}
                            {t.source && t.source !== 'backfill' && <StatusBadge status={t.source} />}
                            {t.last_synced_at && <span>{fmtDate(t.last_synced_at)}</span>}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={t.status} />
                      </td>
                      <td className="px-4 py-2 text-right">
                        <RowMenu tour={t} onAct={(kind, tour) => setAct({ kind, tour })} />
                      </td>
                    </tr>
                  ))}
              {!isLoading && (data?.tours?.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-10 text-center text-sm text-gray-500">
                    No tours match these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className="border-t border-gray-200 px-4 py-3">
            <Pagination
              total={data?.total ?? 0}
              page={page}
              itemsPerPage={limit}
              limit={limit}
              setPage={handlePageChange}
              setItemsPerPage={handleLimitChange}
              showButtons
              showSelect
              selectOptions={[10, 20, 30, 50]}
            />
          </div>
        </div>
      )}

      <TourDetailDrawer vtid={openVtid} onClose={() => setOpenVtid(null)} />
      <ModelPreviewDialog open={preview != null} onClose={() => setPreview(null)} id={preview?.external_id ?? ''} name={preview?.listing_name ?? null} />
      <ArchiveDialog open={act?.kind === 'archive'} onClose={() => setAct(null)} target={{ vtid: act?.tour.vtid }} />
      <ReactivateDialog open={act?.kind === 'reactivate'} onClose={() => setAct(null)} target={{ vtid: act?.tour.vtid }} />
      <VisibilityDialog open={act?.kind === 'visibility'} onClose={() => setAct(null)} target={{ vtid: act?.tour.vtid }} current={act?.tour.visibility} />
      {act != null && (
        <>
          <UnlinkDialog open={act.kind === 'unlink'} onClose={() => setAct(null)} vtid={act.tour.vtid} />
          <RenameDialog open={act.kind === 'details'} onClose={() => setAct(null)} vtid={act.tour.vtid} name={act.tour.listing_name} />
          <AddressVisibilityDialog open={act.kind === 'address'} onClose={() => setAct(null)} vtid={act.tour.vtid} />
        </>
      )}
    </div>
  );
};

export default ToursTab;
